import { createDataConnectorSchema } from '@repo/shared-types';

import type {
  CreateDataConnectorDto,
  DataConnectorConfig,
  DataConnectorDetail,
  FieldMapping,
} from '@repo/shared-types';

export interface KeyValue {
  key: string;
  value: string;
}

export interface SecretRow {
  name: string;
  /** Typed value; '' on a saved secret means "keep what's stored". */
  value: string;
  saved: boolean;
}

export interface SplitLevelDraft {
  param: string;
  facet_params: KeyValue[];
  buckets_path: string;
  value_key: string;
  count_key: string;
}

/** Editable form state; converted to the API DTO by draftToDto. */
export interface ConnectorDraft {
  name: string;
  description: string;
  is_active: boolean;
  url: string;
  method: 'GET' | 'POST';
  headers: KeyValue[];
  params: KeyValue[];
  secrets: SecretRow[];
  removedSecrets: string[];
  records_path: string;
  total_path: string;
  pagination: DataConnectorConfig['pagination'];
  split: SplitLevelDraft[];
  mapping: FieldMapping[];
  /** Mark cars the source stopped listing unavailable without asking. */
  auto_retire: boolean;
  schedule_enabled: boolean;
  schedule_interval_days: number;
  schedule_time: string;
}

export const emptyDraft = (): ConnectorDraft => ({
  name: '',
  description: '',
  is_active: true,
  url: '',
  method: 'GET',
  headers: [],
  params: [],
  secrets: [],
  removedSecrets: [],
  records_path: '',
  total_path: '',
  pagination: {
    mode: 'offset',
    param: 'start',
    size_param: 'rows',
    page_size: 50,
    first_page: 1,
    max_depth: 10000,
    delay_ms: 250,
  },
  split: [],
  mapping: [{ target: 'external_id', path: 'id', transform: 'text' }],
  auto_retire: false,
  schedule_enabled: false,
  schedule_interval_days: 7,
  schedule_time: '02:00',
});

const toRecord = (rows: KeyValue[]): Record<string, string> =>
  Object.fromEntries(rows.filter((r) => r.key.trim()).map((r) => [r.key.trim(), r.value]));

const toRows = (rec: Record<string, string> | undefined): KeyValue[] =>
  Object.entries(rec ?? {}).map(([key, value]) => ({ key, value }));

export const draftToConfig = (d: ConnectorDraft): DataConnectorConfig => ({
  request: {
    url: d.url.trim(),
    method: d.method,
    headers: toRecord(d.headers),
    params: toRecord(d.params),
  },
  records_path: d.records_path.trim(),
  ...(d.total_path.trim() ? { total_path: d.total_path.trim() } : {}),
  pagination: d.pagination,
  split: d.split.map((s) => ({
    param: s.param.trim(),
    facet_params: toRecord(s.facet_params),
    buckets_path: s.buckets_path.trim(),
    value_key: s.value_key.trim() || 'item',
    count_key: s.count_key.trim() || 'count',
  })),
  mapping: d.mapping.map((m) => ({ ...m, path: m.path.trim() })),
  unlisted_cars: d.auto_retire ? 'auto' : 'ask',
});

/** Secrets to send: typed values, plus '' for removed saved secrets (deletes them). */
export const draftSecrets = (d: ConnectorDraft): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const s of d.secrets) if (s.name.trim() && s.value !== '') out[s.name.trim()] = s.value;
  for (const name of d.removedSecrets) out[name] = '';
  return out;
};

export const draftToDto = (d: ConnectorDraft): CreateDataConnectorDto => ({
  name: d.name.trim(),
  description: d.description.trim() || undefined,
  is_active: d.is_active,
  config: draftToConfig(d),
  secrets: draftSecrets(d),
  schedule_enabled: d.schedule_enabled,
  schedule_interval_days: d.schedule_interval_days,
  schedule_time: d.schedule_time,
});

export const detailToDraft = (c: DataConnectorDetail): ConnectorDraft => ({
  name: c.name,
  description: c.description ?? '',
  is_active: c.is_active,
  url: c.config.request.url,
  method: c.config.request.method,
  headers: toRows(c.config.request.headers),
  params: toRows(c.config.request.params),
  secrets: c.secret_names.map((name) => ({ name, value: '', saved: true })),
  removedSecrets: [],
  records_path: c.config.records_path,
  total_path: c.config.total_path ?? '',
  pagination: c.config.pagination,
  split: c.config.split.map((s) => ({
    param: s.param,
    facet_params: toRows(s.facet_params),
    buckets_path: s.buckets_path,
    value_key: s.value_key,
    count_key: s.count_key,
  })),
  mapping: c.config.mapping,
  auto_retire: c.config.unlisted_cars === 'auto',
  schedule_enabled: c.schedule_enabled,
  schedule_interval_days: c.schedule_interval_days,
  schedule_time: c.schedule_time,
});

const formatIssues = (issues: Array<{ path: PropertyKey[]; message: string }>): string[] =>
  issues.map((i) => `${i.path.map(String).join('.') || 'form'}: ${i.message}`);

/** Human-readable problems ("config.request.url: Invalid URL"), empty when valid. */
export const validateDraft = (d: ConnectorDraft): string[] => {
  const result = createDataConnectorSchema.safeParse(draftToDto(d));
  return result.success ? [] : formatIssues(result.error.issues);
};

export type ParseJsonResult = { ok: true; draft: ConnectorDraft } | { ok: false; errors: string[] };

/**
 * Pasted connector JSON (the same shape the API accepts) → form draft.
 * Saved secrets the JSON doesn't mention are kept as "saved".
 */
export const parseConnectorJson = (
  text: string,
  savedSecretNames: string[] = [],
): ParseJsonResult => {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    return { ok: false, errors: [`Not valid JSON: ${(error as Error).message}`] };
  }
  const result = createDataConnectorSchema.safeParse(raw);
  if (!result.success) return { ok: false, errors: formatIssues(result.error.issues) };

  const dto = result.data;
  const typed = Object.entries(dto.secrets).filter(([, v]) => v !== '');
  const typedNames = new Set(typed.map(([name]) => name));
  const draft = detailToDraft({
    ...dto,
    id: '',
    description: dto.description ?? null,
    secret_names: [],
    status: 'idle',
    last_run_at: null,
    last_run_status: null,
    listing_count: 0,
    created_at: '',
    updated_at: '',
  });
  draft.secrets = [
    ...typed.map(([name, value]) => ({ name, value, saved: false })),
    ...savedSecretNames
      .filter((name) => !typedNames.has(name))
      .map((name) => ({ name, value: '', saved: true })),
  ];
  return { ok: true, draft };
};

/** Current form as pasteable JSON; typed secrets included, saved ones never shown. */
export const draftToJson = (d: ConnectorDraft): string =>
  JSON.stringify(
    { ...draftToDto(d), secrets: draftSecrets({ ...d, removedSecrets: [] }) },
    null,
    2,
  );
